import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertService } from 'src/core/services/alert.service';
import { BatchService } from 'src/core/services/batch.service';
import { DirtinessService } from 'src/core/services/dirtiness.service';
import { Company } from 'src/interfaces/company';
import { Individual } from 'src/interfaces/individual';
import { Order } from 'src/interfaces/order';
import {
  FulfilmentItem,
  FulfilmentPayload,
  OrderItem,
  FocusedInput,
  OrderItemStatus
} from 'src/interfaces/order-item';

import { debounce } from 'rxjs/operators';
import { interval } from 'rxjs';

@Component({
  selector: 'app-order-fulfilment',
  templateUrl: './order-fulfilment.component.html',
  styleUrls: ['./order-fulfilment.component.scss']
})
export class OrderFulfilmentComponent implements OnInit {
  order: Order | null = null;
  orderItems: OrderItem[] = [];
  orderItemStatuses: OrderItemStatus[] = [];
  scannedFulfilmentItems: FulfilmentItem[] = [];
  scannedFulfilmentItemsDisplayedColumns: string[] = [
    'name',
    'status',
    'scanned',
    'details'
  ];
  unscannedFulfilmentItems: FulfilmentItem[] = [];
  unScannedFulfilmentItemsDisplayedColumns: string[] = [
    'name',
    'barcode',
    'medicine_number',
    'scanned',
    'action',
    'details'
  ];
  totalUnscannedItems: number = 0;

  constructor(
    private _formBuilder: FormBuilder,
    private _router: Router,
    private _route: ActivatedRoute,
    private _batchService: BatchService,
    private _alertService: AlertService,
    private _dirtinessService: DirtinessService
  ) { }

  ngOnInit(): void {

    this
      ._route
      .data
      .subscribe(data => {
        this.order = data.order.order;
        this.orderItemStatuses = data.order.extras.order_item_status;
        if (!this.order) return;
        this.updateView(this.order);
      });
  }

  ngAfterViewInit(): void {
    //When first load add focus on first unscaned fulfilment item
    this.focusBarcode(this.unscannedFulfilmentItems[0]);
  }

  async scanItem(item: FulfilmentItem) {

    const payload: FulfilmentPayload = {
      barcode: item.barcodeInput.value,
      medicine_number: item.medicineNumberInput.value
    }

    if (!item.orderItem.id) {
      this._alertService
        .warningAlert("Please save the order first");
      return;
    }


    // scan all the order items
    const success: boolean = await this._batchService
      .scanOrderItem(item.orderItem.id, payload);

    item.scanned = success ? item.scanned + 1 : item.scanned;

    item.errored = !success;

    if (success) {
      // Start by focusing the barcode 
      this.focusBarcode(item);

      item.barcodeInput.reset();
      item.barcodeInput.markAsPristine();
      item.medicineNumberInput.reset();
      item.medicineNumberInput.markAsPristine();
    }
    if (item.errored) {
      this.focusBarcode(item);
      this._dirtinessService.setDirty(true);
    }

    if (item.scanned === item.orderItem.quantity) {

      const fulfilled: boolean = await this._batchService
        .fulfillOrderItem(item.orderItem.id);

      if (fulfilled) {
        this.onFinishedScanning(item);
        this._dirtinessService.setDirty(false);
      }

    }
  }

  updateView(order: Order | null) {
    if (!order) return;
    this.orderItems = this.getOrderItems(order);

    const scannedOrderItems: OrderItem[] = this.orderItems
      .filter(item => {

        // Order items without status will be ignored
        if (!item.status) return false;

        return item.status.code === "PKD";
      });

    const unscannedOrderItems: OrderItem[] = this.orderItems
      .filter(item => {

        // Order items without status will be ignored
        if (!item.status) return false;
        return item.status.code !== "PKD";
      });

    this.unscannedFulfilmentItems = unscannedOrderItems.map(item => {

      const fulfilmentItem: FulfilmentItem = {
        orderItem: item,
        barcodeInput: this._formBuilder
          .control('',
            Validators.compose([
              Validators.required,
              Validators.pattern(
                /^([A-Z]{3,10}[0-9]{3,10}-[0-9]{3,10})$/
              )
            ])
          ),
        barcodeInputId: `barcodeInput${item.id}`,
        medicineNumberInput: this._formBuilder
          .control('', Validators.compose([
            Validators.required,
            Validators.pattern(/^([A-Z]{1,10}[0-9]{1,10}-[0-9]{1,10})$/)
          ])),
        medicineNumberInputId: `medicineNumberInput${item.id}`,
        scanned: item.quantity_fulfilled,
        errored: false,
        activated: false,
        focusedInput: FocusedInput.None
      }

      fulfilmentItem.barcodeInput
        .valueChanges
        .pipe(
          debounce(i => interval(500))
        )
        .subscribe((value) => {
          (async () => {
            if (fulfilmentItem.barcodeInput.valid) {

              if (fulfilmentItem.medicineNumberInput.valid) {
                await this.scanItem(fulfilmentItem); // Send a request to the scanning endpoint 
              }
              else
                this.focusMedicineNumber(fulfilmentItem);
            }
          })();
        });

      fulfilmentItem.medicineNumberInput
        .valueChanges
        .pipe(
          debounce(i => interval(500))
        )
        .subscribe((value) => {
          (async () => {
            if (fulfilmentItem.medicineNumberInput.valid) {
              if (fulfilmentItem.barcodeInput.valid) {

                await this.scanItem(fulfilmentItem);

              }
              else
                this.focusBarcode(fulfilmentItem);
            }
          })();
        });

      return fulfilmentItem;
    });

    this.scannedFulfilmentItems = scannedOrderItems.map(item => ({
      orderItem: item,
      barcodeInput: this._formBuilder
        .control('',
          Validators.compose([
            Validators.required,
            Validators.pattern(
              "^([A-Z]{3}[0-9]{3,10}-[0-9]{3,10})$"
            )
          ])
        ),
      barcodeInputId: `barcodeInput${item.id}`,
      medicineNumberInput: this._formBuilder
        .control('', Validators.compose([
          Validators.required,
          Validators.pattern('^([0-9]{1,10})$')
        ])),
      medicineNumberInputId: `medicineNumberInput${item.id}`,
      scanned: item.quantity_fulfilled,
      errored: false,
      activated: false,
      focusedInput: FocusedInput.None
    }));

  }

  getOrderItems(order: Order) {
    if (!order) return [];

    return order
      .order_items
      .map((item: any) => {

        const orderedItem = {
          id: item.id,
          medicineDosagePackageType: item.medicine_dosage_package_type,
          dosageType: item.medicine_dosage_package_type.medicine_dosage_type,
          status: item.status,
          medicine: item.medicine_dosage_package_type.medicine_dosage_type.medicine,
          quantity: item.total_quantity,
          medicine_dosage_package_type_id: item.medicine_dosage_package_type_id,
          quantity_fulfilled: item.quantity_fulfilled,
          batch_items: item.batch_items,
          deleted_at: undefined
        }

        orderedItem.dosageType.default_price = item.medicine_dosage_package_type.medicine_dosage_type.default_price;

        return orderedItem;
      });
  }

  onFinishedScanning(item: FulfilmentItem) {
    // on finished scanning 
    const index = this.unscannedFulfilmentItems.indexOf(item);

    if (index > -1) {
      this.unscannedFulfilmentItems.splice(index, 1);
      this.unscannedFulfilmentItems = [...this.unscannedFulfilmentItems];
    }

    // add to scanned items 
    item.activated = false;


    // find the packed order item status
    const packedStatus: OrderItemStatus | undefined = this.orderItemStatuses
      .find(status => status.code === "PKD");
    if (!packedStatus)
      this._alertService.errorAlert("Could not updated the status of the order item since the status was not found.");
    else
      item.orderItem.status = packedStatus;

    this.scannedFulfilmentItems.push(item);
    this.scannedFulfilmentItems = [...this.scannedFulfilmentItems];
  }

  get company(): Company | null {

    if (!this.order) return null;

    if (this.order.patient)
      return this.order.patient.company;

    if (this.order.client)
      return this.order.client;

    if (this.order.doctor)
      return this.order.doctor.company;

    return null;

  }

  get individual(): Individual | null {
    if (!this.order) return null;
    if (this.order.patient) return this.order.patient;
    if (this.order.doctor) return this.order.doctor;
    return null;
  }

  get allScanned(): boolean {
    return this.scannedFulfilmentItems.length == this.orderItems.length;
  }

  async gotoPrescriptionReview() {

    if (!this.order) {
      this._router.navigate(["/space-dust-not-on-earth"]); // makes sense
      return;
    }

    this._router.navigate(["/portal/prescriptions/review/", this.order.id])
  }

  focusBarcode(item: FulfilmentItem) {
    const input: HTMLInputElement = <HTMLInputElement>document.querySelector(`#${item.barcodeInputId}`);
    input.focus();
    item.focusedInput = FocusedInput.Barcode;
  }

  focusMedicineNumber(item: FulfilmentItem) {
    const input: HTMLInputElement = <HTMLInputElement>document.querySelector(`#${item.medicineNumberInputId}`);
    input.focus();
    item.focusedInput = FocusedInput.MedicineNumber;
  }


}
